import type { Block, LayoutFn, Line } from "./base";
import { normalizedWidth } from "./base";
import { padLine } from "./text";

function fmtNum(n: number): string {
  if (Math.abs(n) >= 1000) return (n / 1000).toFixed(1) + "k";
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(1);
}

export function chart(
  data: number[],
  opts?: { height?: number; color?: string; axisColor?: string; fill?: boolean },
): LayoutFn {
  return (w) => {
    const width = normalizedWidth(w);
    const height = opts?.height ?? 8;
    const color = opts?.color;
    const axisColor = opts?.axisColor;

    if (data.length === 0 || width < 4 || height < 2) return [padLine([], width)];

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;

    const minLabel = fmtNum(min);
    const maxLabel = fmtNum(max);
    const labelW = Math.max(minLabel.length, maxLabel.length);
    const axisW = labelW + 2;
    const plotW = Math.max(1, width - axisW);

    // Resample data to plotW points
    const points: number[] = [];
    if (data.length === 1) {
      for (let i = 0; i < plotW; i++) points.push(data[0]!);
    } else {
      for (let i = 0; i < plotW; i++) {
        const idx = (i / (plotW - 1)) * (data.length - 1);
        const lo = Math.floor(idx);
        const hi = Math.min(lo + 1, data.length - 1);
        const t = idx - lo;
        points.push(data[lo]! * (1 - t) + data[hi]! * t);
      }
    }

    // Map to row indices (0 = bottom, height-1 = top)
    const rows = points.map((v) => Math.round(((v - min) / range) * (height - 1)));

    const result: Block = [];
    for (let row = height - 1; row >= 0; row--) {
      const line: Line = [];

      // Y axis
      let label = "";
      if (row === height - 1) label = maxLabel;
      else if (row === 0) label = minLabel;
      line.push({ text: label.padStart(labelW) + " \u2502", color: axisColor, noSelect: true });

      // Plot area
      let text = "";
      for (let col = 0; col < plotW; col++) {
        const val = rows[col]!;
        if (val === row) text += "\u2584";
        else if (opts?.fill && val > row) text += "\u2588";
        else text += " ";
      }
      line.push(color ? { text, color } : text);
      result.push(padLine(line, width));
    }

    // X axis
    const xAxis: Line = [
      { text: " ".repeat(labelW) + " \u2514" + "\u2500".repeat(plotW), color: axisColor, noSelect: true },
    ];
    result.push(padLine(xAxis, width));

    return result;
  };
}
